import routes from '@config/routes'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { Typography, Box, Grid } from '@mui/material'
import useStyles from '@styles/layout/layout'
import { useRouter } from 'next/router'
import { useAuth } from '@contexts/auth'
import FreshworksWidget from '@components/Support/FreshdeskWidget'
import YbugScript from '@components/Support/ybugWidget'
import React, { ReactNode } from 'react'

interface LayoutProps {
  /** Page title */
  title?: string
  /** Optional subtitle */
  subtitle?: string | null
  /** Optional description text */
  description?: string | null
  /** Navigation tabs component */
  tabs?: ReactNode | null
  /** Filter components */
  filters?: ReactNode | null
  /** Additional header content */
  extraHeader?: ReactNode | null
  /** Page content */
  children: ReactNode
}

/**
 * Main layout component for page structure
 */
const Layout: React.FC<LayoutProps> = ({
  title = 'Title',
  subtitle = null,
  description = null,
  tabs = null,
  filters = null,
  extraHeader = null,
  children
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const router = useRouter()
  const { user } = useAuth()
  const theme = useTheme()

  const currentURL = typeof window !== 'undefined' ? window.location.href : ''

  const isPageAccount = router.asPath.includes(routes.account.root)
  const isTenantPage =
    user.role === 'tenant' || currentURL.includes('tenant/preview')
  const isProjectsPage = router.asPath.includes(routes.projects.summary)
  const currentTab =
    currentURL.split('/').pop() === 'contracts'
      ? t('contracts_')
      : currentURL.split('/').pop() === 'landlords'
      ? t('landlords_')
      : currentURL.split('/').pop() === 'internal'
      ? ''
      : t(currentURL.split('/').pop() || '')

  return (
    <>
      <Grid container className={classes.root}>
        {title !== t('dashboard') && (
          <Grid item xs={12} className={classes.headerContainer}>
            <Box
              className={
                isTenantPage
                  ? classes.overflowingContainerAccount
                  : !isPageAccount
                  ? classes.overflowingContainer
                  : classes.overflowingContainerAccount
              }
            >
              <Grid
                item
                xs={12}
                sx={{
                  borderRadius: tabs
                    ? `${theme.spacing(1)} ${theme.spacing(1)} 0px 0px`
                    : theme.spacing(1),
                  display: isProjectsPage ? 'flex' : undefined,
                  flexDirection: isProjectsPage ? 'row' : undefined,
                  justifyContent: isProjectsPage ? 'space-between' : undefined
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    paddingBottom: theme.spacing(1)
                  }}
                >
                  {isPageAccount && (
                    <Box className={classes.informationContainer}>
                      <Typography
                        variant="h6"
                        color={'dark.main'}
                        fontWeight={600}
                        data-testid="Typography-e3306e19-3e8c-4475-8895-6a59b84719fb"
                      >
                        {subtitle ? `${title} - ` : title}
                        <span
                          style={{ color: theme.palette.primary.main }}
                          data-testid="span-f2d517a6-597a-4c6d-9887-34ac2ef16eb8"
                        >
                          {subtitle}
                        </span>
                      </Typography>

                      {description && (
                        <Typography
                          variant="caption"
                          color="text.light"
                          data-testid="Typography-2148077b-55ff-43b0-9104-eaa5bfecf7dd"
                        >
                          {description}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {!isPageAccount && (
                    <Box className={classes.informationContainer}>
                      {subtitle && (
                        <Typography
                          variant="overline"
                          color="secondary"
                          data-testid="Typography-c4683d62-677c-4479-9afe-b5b525b2ac44"
                        >
                          {subtitle}
                        </Typography>
                      )}

                      <Typography
                        variant="h6"
                        color={'dark.main'}
                        fontWeight={600}
                        data-testid="Typography-76752b6e-064f-4392-a1bf-bdea1a4ec840"
                      >
                        {title}
                        {tabs && currentTab && currentTab !== title && (
                          <span
                            style={{ color: theme.palette.grey[900] }}
                            data-testid="span-cf1745ab-de5f-465b-87ce-b54133ebd41c"
                          >
                            {' '}
                            - {currentTab}
                          </span>
                        )}
                      </Typography>

                      {description && (
                        <Typography
                          variant="caption"
                          color="text.light"
                          data-testid="Typography-2dcc79e4-b1dd-456a-9de6-ec5188b60d85"
                        >
                          {description}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {extraHeader && <Box sx={{ mr: 3 }}>{extraHeader}</Box>}
                </Box>
                {tabs}
                {filters && (
                  <Grid item xs={12} className={classes.filters}>
                    {filters}
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} className={classes.childContainer}>
          <Box
            className={
              isTenantPage
                ? classes.overflowingContainerAccount
                : !isPageAccount
                ? classes.overflowingContainer
                : classes.overflowingContainerAccount
            }
          >
            {children}
          </Box>
        </Grid>
      </Grid>
      <FreshworksWidget user={user} />
      <YbugScript user={user} />
    </>
  )
}

export default Layout
